/**
 * SG Incorporation (Pre) — 문서별 [[Signers]] 컨텍스트.
 *
 * Pre 단계 엔티티는 L_ENTITY 가 아직 없어 icpCd 로 officer API 를 치면 AUTH002 → 리다이렉트됨.
 * Signers UI 에는 entityCd 를 넘기지 않고, 폼의 directors 로 시드 + 수동 추가만 사용 (MY Inc Doc 패턴).
 */
import { inject, provide, reactive, ref, type InjectionKey, type Ref } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export type SgPreSignersCtx = {
  /** SignersField 용 — Pre 에서는 항상 '' (officer API 미호출) */
  entityCd: Ref<string>;
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  /** Create Task / gather 용 스냅샷 */
  exportAll: () => Record<string, SignerEntry[]>;
  importAll: (map: Record<string, SignerEntry[]> | null | undefined) => void;
  /** 비어 있는 문서 Signers 에 directors 시드 */
  seedFromValuesIfEmpty: (values: Record<string, any>) => void;
};

export const SG_PRE_SIGNERS: InjectionKey<SgPreSignersCtx> = Symbol('sgIncPreSigners');

/** Engagement / Constitution / Client Acceptance / ACRA Risk Signature 문서의 [[Signers]] varKey */
export const SG_PRE_SIGNER_KEYS = {
  engagement: 'engagement_Signers',
  constitution: 'constitution_Signers',
  client: 'client_Signers',
  /** ACRA Recommendation Signature */
  riskRecommend: 'risk_recommend_Signers',
  /** ACRA Approval Signature */
  riskApprove: 'risk_approve_Signers',
} as const;

/** 문서 본문 [[Signers]] 용 — directors 시드 대상 (Risk officer 서명은 별도) */
const SG_PRE_SIGNER_DOC_KEYS = [
  SG_PRE_SIGNER_KEYS.engagement,
  SG_PRE_SIGNER_KEYS.constitution,
  SG_PRE_SIGNER_KEYS.client,
] as const;

function officerSignerEntry(
  id: string,
  name: string,
  title: string,
): SignerEntry {
  return {
    id,
    name,
    title: title || '',
    role: 'officer',
    isManual: true,
    showTitle: !!title,
    _signerLayout: 'box',
  };
}

/**
 * 회사 단위 문서(First Board · Incorporation Documents · Engagement · CAF · Constitution)의
 * 기본 서명자 = CAF 이사 전원.
 *   ⚠ 예전엔 `dir{i} || f45_{i}/director_name` 을 같이 봐서, CAF 에서 지운 이사가 Form 45
 *     사본 값으로 되살아나고(삭제 이사 잔존), 행 id 가 어긋나면 같은 이사가 두 번 들어갔다.
 *     → CAF 이사(dir{i})만 보고, 이름 기준으로 중복 제거. CAF 가 비었을 때만 Form 45 로 대체.
 */
export function buildSgPreSignersFromValues(values: Record<string, any>): SignerEntry[] {
  const out: SignerEntry[] = [];
  const src = values && typeof values === 'object' ? values : {};
  const seen = new Set<string>();
  const hasCafDirector = Array.from({ length: 30 }, (_, i) => i + 1)
    .some((i) => String(src[`dir${i}_director_name`] || '').trim());
  for (let i = 1; i <= 30; i++) {
    const name = String(
      hasCafDirector ? src[`dir${i}_director_name`] || '' : src[`f45_${i}/director_name`] || '',
    ).trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push({
      id: `sg-pre-dir-${i}`,
      name,
      title: 'Director',
      role: 'director',
      email: String(src[`dir${i}_director_email`] || '').trim(),
      isManual: true,
      showTitle: true,
      _signerLayout: 'box',
    });
  }
  if (!out.length) {
    const bare = String(src.director_name || '').trim();
    if (bare) {
      out.push({
        id: 'sg-pre-dir-1',
        name: bare,
        title: 'Director',
        role: 'director',
        email: String(src.director_email || '').trim(),
        isManual: true,
        showTitle: true,
        _signerLayout: 'box',
      });
    }
  }
  return out;
}

/**
 * Constitution 서명자 = **가입자(subscribers) = CAF 주주 전원**.
 *   정관 서명선은 [[shareholder_name_1/2]] (주주)인데 T-Sign 서명자는 이사로 잡혀
 *   서명선과 서명자가 서로 다른 사람이었다(N-10).
 */
export function buildSgPreSubscriberSigners(values: Record<string, any>): SignerEntry[] {
  const out: SignerEntry[] = [];
  const src = values && typeof values === 'object' ? values : {};
  const seen = new Set<string>();
  for (let i = 1; i <= 50; i++) {
    const name = String(src[`sh${i}_shareholder_name`] || '').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push({
      id: `sg-pre-sh-${i}`,
      name,
      title: 'Subscriber',
      role: 'shareholder',
      email: String(src[`sh${i}_shareholder_email`] || '').trim(),
      isManual: true,
      showTitle: true,
      _signerLayout: 'box',
    });
  }
  return out;
}

export function provideSgPreSigners(_entityCdIgnored?: Ref<string>): SgPreSignersCtx {
  const store = reactive<Record<string, SignerEntry[]>>({});
  /** Pre: L_ENTITY 없음 — officer 목록 API 호출 방지 */
  const signerEntityCd = ref('');

  const ctx: SgPreSignersCtx = {
    entityCd: signerEntityCd,
    signersOf: (varKey: string) => store[varKey] || [],
    setSigners: (varKey: string, list: SignerEntry[]) => {
      store[varKey] = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
    },
    exportAll: () => {
      const out: Record<string, SignerEntry[]> = {};
      for (const [k, v] of Object.entries(store)) {
        if (v?.length) out[k] = v.map((s) => ({ ...s }));
      }
      return out;
    },
    importAll: (map) => {
      for (const k of Object.keys(store)) delete store[k];
      if (!map || typeof map !== 'object') return;
      for (const [k, v] of Object.entries(map)) {
        if (Array.isArray(v)) store[k] = v.map((s) => ({ ...s }));
      }
    },
    /**
     * 비어 있으면 기본 서명자로 채우고, **자동으로 넣었던 목록은 최신 CAF 로 갱신**한다.
     *   자동 목록 = 항목 id 가 전부 sg-pre-dir-* / sg-pre-f45-* (시드가 만든 것).
     *   사용자가 서명자 피커로 고른 목록(다른 id)은 건드리지 않는다.
     *   (갱신이 없으면 CAF 에서 이름을 바꾸거나 이사를 지워도 옛 서명자가 남았다 — N-3/N-4)
     */
    seedFromValuesIfEmpty: (values) => {
      const isAuto = (list: SignerEntry[] | undefined, prefix: string) =>
        !!list?.length && list.every((s) => String(s.id || '').startsWith(prefix));
      const defaults = buildSgPreSignersFromValues(values);
      const subscribers = buildSgPreSubscriberSigners(values);
      for (const key of SG_PRE_SIGNER_DOC_KEYS) {
        if (key === SG_PRE_SIGNER_KEYS.constitution) {
          // 정관은 가입자(주주)가 서명 — 예전에 이사로 자동 시드된 목록(sg-pre-dir-*)도 교체한다.
          //   주주가 아직 없으면 이사 기본값 유지.
          const list = subscribers.length ? subscribers : defaults;
          if (!store[key]?.length || isAuto(store[key], 'sg-pre-dir-') || isAuto(store[key], 'sg-pre-sh-')) {
            store[key] = list.map((s) => ({ ...s }));
          }
          continue;
        }
        if (!store[key]?.length || isAuto(store[key], 'sg-pre-dir-')) {
          store[key] = defaults.map((s) => ({ ...s }));
        }
      }
      // Form 45 — 이사별 부수(f45_{id}/)의 서명은 그 이사 본인으로 시드.
      const src = values && typeof values === 'object' ? values : {};
      for (let i = 1; i <= 30; i++) {
        const name = String(src[`f45_${i}/director_name`] || '').trim();
        const key = `f45_${i}/director_Signers`;
        if (isAuto(store[key], 'sg-pre-f45-') && store[key][0]?.name !== name) delete store[key];
        if (name && !store[key]?.length) {
          store[key] = [
            {
              id: `sg-pre-f45-${i}`,
              name,
              title: 'Director',
              role: 'director',
              email: String(src[`f45_${i}/director_email`] || '').trim(),
              isManual: true,
              showTitle: true,
              _signerLayout: 'box',
            },
          ];
        }
      }
      // ACRA Risk Signature — officer 성명으로 시드 (Signers UI)
      //   자동으로 넣은 서명자(id sg-pre-rec/app-officer)는 이름·직위를 고칠 때마다 따라간다.
      //   (예전엔 비어 있을 때만 넣어서 첫 글자 입력 순간의 한 글자만 서명자로 남고 이후 수정이 반영 안 됨)
      //   서명자 피커로 직접 고른 목록(다른 id)은 건드리지 않는다.
      const officerSeed = (key: string, id: string, name: string, title: string) => {
        const cur = store[key];
        if (cur?.length && !isAuto(cur, id)) return; // 사용자가 고른 서명자
        if (!name) {
          if (cur?.length) delete store[key];
          return;
        }
        if (cur?.length === 1 && cur[0].name === name && String(cur[0].title || '') === title) return;
        store[key] = [officerSignerEntry(id, name, title)];
      };
      officerSeed(
        SG_PRE_SIGNER_KEYS.riskRecommend, 'sg-pre-rec-officer',
        String(values?.recommending_officer_name || '').trim(),
        String(values?.recommending_officer_position || '').trim(),
      );
      officerSeed(
        SG_PRE_SIGNER_KEYS.riskApprove, 'sg-pre-app-officer',
        String(values?.approving_officer_name || '').trim(),
        String(values?.approving_officer_position || '').trim(),
      );
    },
  };

  provide(SG_PRE_SIGNERS, ctx);
  return ctx;
}

export function useSgPreSigners(): SgPreSignersCtx {
  const ctx = inject(SG_PRE_SIGNERS, null);
  if (!ctx) {
    const emptyCd = ref('');
    return {
      entityCd: emptyCd,
      signersOf: () => [],
      setSigners: () => {},
      exportAll: () => ({}),
      importAll: () => {},
      seedFromValuesIfEmpty: () => {},
    };
  }
  return ctx;
}
